import {
  addToCart,
  addVariantToCart,
  applyBackToCollectionLink,
  applyContinueShoppingLinks,
  buildCartAddPayload,
  buildQuickViewUrl,
  canAddQuantity,
  changeLineItem,
  fetchCart,
  formatCartCount,
  formatMoney,
  getRememberedVariantMax,
  parseMaxQuantity,
  rememberContinueShoppingUrl,
  rememberVariantMax,
  resolveBackToCollectionUrl,
  resolveCartErrorMessage,
  resolveContinueShoppingUrl,
  syncQuickAddButtons,
  syncVariantMaxFromDom,
  updateCartCountBadge,
  variantQuantityInCart,
} from '#cart-api';
import { initProductGallery } from '#product-gallery';
import {
  buildCartLineHtml,
  closeCartDrawer,
  closeQuickViewModal,
  getCartUrls,
  getDrawer,
  getQuickViewModal,
  openCartDrawer,
  openQuickViewModal,
  renderCartDrawer,
  renderCartPage,
  showToast,
} from '#cart-render';

export {
  addToCart,
  addVariantToCart,
  applyBackToCollectionLink,
  applyContinueShoppingLinks,
  buildCartAddPayload,
  buildCartLineHtml,
  buildQuickViewUrl,
  canAddQuantity,
  changeLineItem,
  closeCartDrawer,
  closeQuickViewModal,
  fetchCart,
  formatCartCount,
  formatMoney,
  openCartDrawer,
  openQuickViewModal,
  parseMaxQuantity,
  rememberContinueShoppingUrl,
  renderCartDrawer,
  renderCartPage,
  resolveBackToCollectionUrl,
  resolveCartErrorMessage,
  resolveContinueShoppingUrl,
  updateCartCountBadge,
  variantQuantityInCart,
};

let changeInFlight = false;
let quickViewAbort = null;

async function applyCartState(cart, { openDrawer = false, closeQuickView = false } = {}) {
  if (!cart) return;
  syncVariantMaxFromDom();
  updateCartCountBadge(cart.item_count);
  renderCartDrawer(cart);
  renderCartPage(cart);
  syncQuickAddButtons(cart);
  if (closeQuickView) closeQuickViewModal();
  if (openDrawer) openCartDrawer();
}

async function handleAddResult(result, errorMessage, { fromQuickView = false } = {}) {
  if (!result.ok) {
    showToast(resolveCartErrorMessage(result.error, errorMessage), {
      isError: true,
    });
    return false;
  }

  if (result.cart) {
    await applyCartState(result.cart, {
      openDrawer: true,
      closeQuickView: fromQuickView,
    });
  } else {
    if (fromQuickView) closeQuickViewModal();
    openCartDrawer();
  }
  return true;
}

async function handleLineChange(lineRoot, nextQuantity) {
  if (changeInFlight) return;
  const key = lineRoot?.dataset?.lineKey;
  if (!key) return;

  const maxQuantity =
    parseMaxQuantity(lineRoot.dataset.maxQuantity) ??
    getRememberedVariantMax(lineRoot.dataset.variantId);
  const limitMessage =
    document.body.dataset.cartQuantityLimit ||
    document.body.dataset.cartAddError ||
    'Could not update cart';
  const input = lineRoot.querySelector('[data-qty-input]');
  const current = Number(input?.value) || 0;
  let quantity = Math.max(0, Number(nextQuantity) || 0);

  if (maxQuantity !== null && quantity > maxQuantity) {
    showToast(limitMessage, { isError: true });
    if (input) input.value = String(Math.min(current, maxQuantity));
    if (current >= maxQuantity) return;
    quantity = maxQuantity;
  }

  changeInFlight = true;
  const { cartChangeUrl } = getCartUrls();
  const errorMessage =
    document.body.dataset.cartAddError || 'Could not update cart';

  try {
    const result = await changeLineItem(key, quantity, { cartChangeUrl });
    if (!result.ok) {
      showToast(resolveCartErrorMessage(result.error, errorMessage), {
        isError: true,
      });
      return;
    }
    await applyCartState(result.cart);
  } catch {
    showToast(errorMessage, { isError: true });
  } finally {
    changeInFlight = false;
  }
}

function bindQtyControls(root) {
  if (!root) return;

  root.addEventListener('click', (event) => {
    const decrease = event.target.closest('[data-qty-decrease]');
    const increase = event.target.closest('[data-qty-increase]');
    const remove = event.target.closest('[data-cart-remove]');
    const line = event.target.closest('[data-cart-line]');
    if (!line) return;

    const input = line.querySelector('[data-qty-input]');
    const current = Number(input?.value) || 0;

    if (decrease) {
      event.preventDefault();
      handleLineChange(line, Math.max(0, current - 1));
      return;
    }
    if (increase) {
      event.preventDefault();
      handleLineChange(line, current + 1);
      return;
    }
    if (remove) {
      event.preventDefault();
      handleLineChange(line, 0);
    }
  });

  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-qty-input]');
    if (!input) return;
    const line = input.closest('[data-cart-line]');
    if (!line) return;
    handleLineChange(line, Math.max(0, Number(input.value) || 0));
  });
}

function bindDrawerChrome() {
  const drawer = getDrawer();
  if (!drawer) return;

  drawer.querySelectorAll('[data-cart-drawer-close], [data-cart-drawer-overlay]').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.preventDefault();
      closeCartDrawer();
    });
  });

  bindQtyControls(drawer);
}

function bindHeaderCartOpen() {
  document.querySelectorAll('[data-cart-open]').forEach((trigger) => {
    trigger.addEventListener('click', async (event) => {
      event.preventDefault();
      const { cartUrl } = getCartUrls();
      try {
        const cart = await fetchCart({ cartUrl });
        if (cart) await applyCartState(cart, { openDrawer: true });
        else openCartDrawer();
      } catch {
        openCartDrawer();
      }
    });
  });
}

async function submitProductForm(form) {
  const errorMessage =
    document.body.dataset.cartAddError || 'Could not add to cart';
  const limitMessage =
    document.body.dataset.cartQuantityLimit || errorMessage;
  const { cartUrl } = getCartUrls();
  const submitButton = form.querySelector('[name="add"]');
  const fromQuickView = form.hasAttribute('data-quick-view-form');
  const formData = new FormData(form);
  const variantId = formData.get('id');
  const quantity = Math.max(1, Number(formData.get('quantity') || 1) || 1);
  const maxQuantity =
    parseMaxQuantity(form.dataset.maxQuantity) ??
    getRememberedVariantMax(variantId);

  if (submitButton) submitButton.disabled = true;

  try {
    const cart = await fetchCart({ cartUrl });
    const gate = canAddQuantity({
      cart,
      variantId,
      quantity,
      maxQuantity,
    });
    if (!gate.ok) {
      showToast(resolveCartErrorMessage(gate.error, limitMessage), {
        isError: true,
      });
      if (cart) syncQuickAddButtons(cart);
      return;
    }

    const result = await addToCart(formData, {
      cartAddUrl: form.dataset.cartAddUrl || '/cart/add.js',
      cartUrl,
    });
    await handleAddResult(result, errorMessage, { fromQuickView });
  } catch {
    showToast(errorMessage, { isError: true });
  } finally {
    if (submitButton?.dataset.labelAdd) {
      const variantSelect = form.querySelector('[data-variant-select]');
      const isAvailable =
        variantSelect?.selectedOptions[0]?.dataset.available !== 'false';

      submitButton.disabled = !isAvailable;
      submitButton.textContent = isAvailable
        ? submitButton.dataset.labelAdd
        : submitButton.dataset.labelSoldOut;
    } else if (submitButton) {
      submitButton.disabled = false;
    }
  }
}

function bindProductForms() {
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    await submitProductForm(form);
  });
}

async function loadQuickView(productUrl) {
  const modal = getQuickViewModal();
  if (!modal || !productUrl) return;

  const body = modal.querySelector('[data-quick-view-body]');
  const loading = modal.querySelector('[data-quick-view-loading]');
  const errorMessage =
    document.body.dataset.cartAddError || 'Could not load product';

  if (quickViewAbort) quickViewAbort.abort();
  quickViewAbort = new AbortController();

  if (body) body.innerHTML = '';
  if (loading) loading.hidden = false;
  openQuickViewModal();

  try {
    const sectionUrl = buildQuickViewUrl(productUrl);
    const response = await fetch(sectionUrl, {
      headers: { Accept: 'text/html' },
      signal: quickViewAbort.signal,
    });
    if (!response.ok) throw new Error('quick_view_failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('[data-quick-view-content]');

    if (!content || !body) throw new Error('quick_view_empty');
    body.replaceChildren(document.importNode(content, true));
    initProductGallery(body.querySelector('[data-product-gallery]'));
  } catch (error) {
    if (error?.name === 'AbortError') return;
    showToast(errorMessage, { isError: true });
    closeQuickViewModal();
  } finally {
    if (loading) loading.hidden = true;
  }
}

function bindQuickViewAndQuickAdd() {
  const modal = getQuickViewModal();

  document.addEventListener('click', async (event) => {
    const quickViewBtn = event.target.closest('[data-quick-view]');
    if (quickViewBtn) {
      event.preventDefault();
      await loadQuickView(quickViewBtn.dataset.productUrl);
      return;
    }

    const quickAddBtn = event.target.closest('[data-quick-add]');
    if (quickAddBtn) {
      event.preventDefault();
      if (quickAddBtn.disabled) return;

      const variantId = quickAddBtn.dataset.variantId;
      const maxQuantity = parseMaxQuantity(quickAddBtn.dataset.maxQuantity);
      rememberVariantMax(variantId, maxQuantity);
      const errorMessage =
        document.body.dataset.cartAddError || 'Could not add to cart';
      const limitMessage =
        document.body.dataset.cartQuantityLimit || errorMessage;
      const { cartUrl } = getCartUrls();

      quickAddBtn.disabled = true;
      try {
        const cart = await fetchCart({ cartUrl });
        const gate = canAddQuantity({
          cart,
          variantId,
          quantity: 1,
          maxQuantity,
        });
        if (!gate.ok) {
          showToast(resolveCartErrorMessage(gate.error, limitMessage), {
            isError: true,
          });
          if (cart) {
            await applyCartState(cart, { openDrawer: true });
          }
          return;
        }

        const result = await addVariantToCart(variantId, 1, {
          cartAddUrl: quickAddBtn.dataset.cartAddUrl || '/cart/add.js',
          cartUrl,
        });
        await handleAddResult(result, errorMessage);
      } catch {
        showToast(errorMessage, { isError: true });
      } finally {
        const latestCart = await fetchCart({ cartUrl }).catch(() => null);
        if (latestCart) syncQuickAddButtons(latestCart);
        else if (maxQuantity === null) quickAddBtn.disabled = false;
      }
      return;
    }

    if (event.target.closest('[data-quick-view-close]')) {
      event.preventDefault();
      closeQuickViewModal();
      return;
    }

    const qtyRoot = event.target.closest('[data-quick-view-form]');
    if (qtyRoot) {
      const input = qtyRoot.querySelector('input[name="quantity"]');
      if (!input) return;
      const maxQuantity =
        parseMaxQuantity(qtyRoot.dataset.maxQuantity) ??
        parseMaxQuantity(input.max) ??
        getRememberedVariantMax(qtyRoot.querySelector('[name="id"]')?.value);
      if (event.target.closest('[data-quick-qty-decrease]')) {
        event.preventDefault();
        input.value = String(Math.max(1, (Number(input.value) || 1) - 1));
        return;
      }
      if (event.target.closest('[data-quick-qty-increase]')) {
        event.preventDefault();
        const next = Math.max(1, (Number(input.value) || 1) + 1);
        if (maxQuantity !== null && next > maxQuantity) {
          const limitMessage =
            document.body.dataset.cartQuantityLimit ||
            document.body.dataset.cartAddError ||
            'Could not add to cart';
          showToast(limitMessage, { isError: true });
          input.value = String(maxQuantity);
          return;
        }
        input.value = String(next);
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const quickModal = getQuickViewModal();
    if (quickModal?.classList.contains('is-open')) {
      closeQuickViewModal();
      return;
    }
    const drawer = getDrawer();
    if (drawer?.classList.contains('is-open')) {
      closeCartDrawer();
    }
  });

  if (modal) {
    // overlay/close already handled via delegation
  }
}

function bindCartPage() {
  const page = document.querySelector('[data-cart-page]');
  if (!page) return;
  bindQtyControls(page);
  applyContinueShoppingLinks(page);
}

if (typeof document !== 'undefined') {
  rememberContinueShoppingUrl();
  syncVariantMaxFromDom();
  bindProductForms();
  bindDrawerChrome();
  bindHeaderCartOpen();
  bindCartPage();
  bindQuickViewAndQuickAdd();
  applyBackToCollectionLink(document);

  fetchCart()
    .then((cart) => {
      if (cart) syncQuickAddButtons(cart);
    })
    .catch(() => {});
}
